import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import GlassPanel from "@/components/GlassPanel";
import ScouterNumber from "@/components/ScouterNumber";
import PillarRadar from "@/components/PillarRadar";
import EditProfileDialog from "@/components/EditProfileDialog";
import PlayerCard from "@/components/PlayerCard";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { toast } from "sonner";
import { Activity, UserCog, Trash2, Download, X, IdCard, ShieldCheck } from "lucide-react";
import { toPng } from "html-to-image";
import InfoHint from "@/components/InfoHint";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";

const PILLAR_COLORS = {
  gps: "#FFFFFF",
  strength: "#00F0FF",
  speed: "#79F2C0",
  explosive: "#FFD166",
  stamina: "#F08080",
  control: "#C792EA",
};

// CARD_W = base PlayerCard width; wrapper scales it to fit screen on mobile.
// Base ratio is 4:5 (540×675) so a pixelRatio=2 export yields exactly 1080×1350.
const CARD_W = 540;
const CARD_H = 675;

function useCardScale(maxWidthPx = CARD_W) {
  const [scale, setScale] = useState(1);
  useLayoutEffect(() => {
    const calc = () => {
      const padding = 32;
      const w = Math.min(window.innerWidth - padding, maxWidthPx);
      setScale(Math.max(0.4, Math.min(1, w / CARD_W)));
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, [maxWidthPx]);
  return scale;
}

export default function MyGPS() {
  const { user } = useAuth();
  const [profile, setProfile] = useState(null);
  const [values, setValues] = useState({});
  // Body metrics — MANDATORY per submission. Pre-filled from user's last known values.
  const [heightCm, setHeightCm] = useState("");
  const [weightKg, setWeightKg] = useState("");
  const [busy, setBusy] = useState(false);
  const [showInput, setShowInput] = useState(true);
  const [testsMeta, setTestsMeta] = useState([]);
  const [editOpen, setEditOpen] = useState(false);
  // card payload: { source: 'current' | 'log', name, avatarUrl, date, gps, archetype, pillars, country, gym }
  const [cardPayload, setCardPayload] = useState(null);
  const [cardBusy, setCardBusy] = useState(false);
  const cardRef = useRef(null);
  const cardScale = useCardScale();

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/profile");
      setProfile(data);
      if (data.current_gps) setShowInput(false);
      // Pre-fill body metrics from profile (or last score) so users don't retype.
      const u = data.user || {};
      const latest = (data.history || [])[0] || {};
      const preH = u.height_cm ?? latest.height_cm ?? latest.snapshot?.height_cm ?? "";
      const preW = u.weight_kg ?? latest.weight_kg ?? latest.snapshot?.weight_kg ?? "";
      setHeightCm(preH === null ? "" : String(preH));
      setWeightKg(preW === null ? "" : String(preW));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => {
    load();
    api.get("/tests-meta").then((r) => setTestsMeta(r.data.tests || [])).catch(() => {});
  }, [load]);

  useEffect(() => {
    if (!cardPayload) return;
    const onKey = (e) => { if (e.key === "Escape") setCardPayload(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [cardPayload]);

  const gender = user?.gender || "M";
  const set = (k, v) => setValues((s) => ({ ...s, [k]: v }));

  const validate = () => {
    for (const t of testsMeta) {
      const v = values[t.key];
      if (v === "" || v === null || v === undefined) continue;
      const num = parseFloat(v);
      if (Number.isNaN(num)) return `${t.name}: invalid number`;
      if (num < 0) return `${t.name}: negative value not allowed`;
      const [, , r] = gender === "F" ? t.qdr_F : t.qdr_M;
      if (t.lower_better) {
        if (num < r) return `${t.name}: ${num}${t.unit} is below R (${r}${t.unit}). Value must be above R.`;
      } else {
        if (num > r) return `${t.name}: ${num}${t.unit} exceeds R (${r}${t.unit}). Value must be below R.`;
      }
    }
    return null;
  };

  const submit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { toast.error(err); return; }
    const h = parseFloat(heightCm);
    const w = parseFloat(weightKg);
    if (!heightCm || Number.isNaN(h) || h < 80 || h > 250) {
      toast.error("Height must be between 80 and 250 cm");
      return;
    }
    if (!weightKg || Number.isNaN(w) || w < 15 || w > 250) {
      toast.error("Weight must be between 15 and 250 kg");
      return;
    }
    const payload = { height_cm: h, weight_kg: w };
    Object.entries(values).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) payload[k] = parseFloat(v);
    });
    // At least one test value beyond height/weight.
    if (Object.keys(payload).length <= 2) { toast.error("Enter at least one test value"); return; }
    setBusy(true);
    try {
      await api.post("/scores", payload);
      toast.success("TEST RECORDED · GPS UPDATED");
      setValues({});
      await load();
      setShowInput(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  const deleteLog = async (id) => {
    if (!window.confirm("Delete this test log permanently?")) return;
    try {
      await api.delete(`/scores/${id}`);
      toast.success("TEST LOG DELETED");
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
  };

  const openCurrentCard = () => {
    if (!gps) return;
    const latestVerified = !!(profile?.history?.[0]?.verified);
    setCardPayload({
      source: "current",
      name: user?.name,
      avatarUrl: user?.avatar_url,
      date: new Date(),
      gps: gps.gps || 0,
      archetype: gps.archetype,
      pillars: gps.pillars || {},
      country: user?.country,
      gym: user?.gym,
      logId: null,
      verified: latestVerified,
      publicId: user?.public_id || "",
    });
  };

  const openLogCard = (h) => {
    setCardPayload({
      source: "log",
      name: user?.name,
      avatarUrl: user?.avatar_url,
      date: h.created_at ? new Date(h.created_at) : new Date(),
      gps: h.result?.gps || 0,
      archetype: h.result?.archetype,
      pillars: h.result?.pillars || {},
      country: user?.country,
      gym: user?.gym,
      logId: h.id,
      verified: !!h.verified,
      publicId: user?.public_id || "",
    });
  };

  const downloadCard = async () => {
    if (!cardRef.current) return;
    setCardBusy(true);
    try {
      // Base card is 540×675 (4:5). pixelRatio=2 yields exactly 1080×1350.
      const dataUrl = await toPng(cardRef.current, {
        cacheBust: true,
        pixelRatio: 2,
        backgroundColor: "#000000",
        skipFonts: true,
      });
      const a = document.createElement("a");
      a.href = dataUrl;
      const suffix = cardPayload?.source === "log" && cardPayload?.logId
        ? `-log-${cardPayload.logId.slice(0, 6)}`
        : "";
      a.download = `gps-card-${(user?.name || "athlete").replace(/\s+/g, "_")}${suffix}.png`;
      a.click();
      toast.success("GPS CARD DOWNLOADED");
    } catch (err) {
      toast.error("Card export failed: " + (err?.message || ""));
    } finally {
      setCardBusy(false);
    }
  };

  const gps = profile?.current_gps;

  // Chart series — oldest -> newest
  const chartData = useMemo(() => {
    const hist = profile?.history || [];
    return [...hist].reverse().map((h, i) => ({
      idx: i + 1,
      date: new Date(h.created_at).toLocaleDateString(undefined, { month: "short", day: "2-digit" }),
      gps: h.result?.gps ?? 0,
      strength: h.result?.pillars?.strength ?? 0,
      speed: h.result?.pillars?.speed ?? 0,
      explosive: h.result?.pillars?.explosive ?? 0,
      stamina: h.result?.pillars?.stamina ?? 0,
      control: h.result?.pillars?.control ?? 0,
    }));
  }, [profile]);

  // Progress-log series visibility — admin can toggle which lines render.
  const [visibleSeries, setVisibleSeries] = useState({
    gps: true, strength: true, speed: true, explosive: true, stamina: true, control: true,
  });
  const toggleSeries = (k) => setVisibleSeries((s) => ({ ...s, [k]: !s[k] }));
  const PROGRESS_SERIES = [
    { k: "gps",       label: "GPS", w: 2.5 },
    { k: "strength",  label: "STR", w: 1.4 },
    { k: "speed",     label: "SPD", w: 1.4 },
    { k: "explosive", label: "EXP", w: 1.4 },
    { k: "stamina",   label: "STA", w: 1.4 },
    { k: "control",   label: "CTL", w: 1.4 },
  ];

  return (
    <div className="max-w-7xl mx-auto px-6 md:px-12 py-12" data-testid="my-gps-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-full overflow-hidden border border-white/25 bg-black/40 shrink-0" data-testid="my-gps-avatar">
            {user?.avatar_url ? (
              <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full grid place-items-center gps-mono text-mute text-[9px] tracking-[0.25em] text-center px-1">NO<br/>PHOTO</div>
            )}
          </div>
          <div>
            <div className="gps-tag mb-3">ATHLETE TERMINAL</div>
            <h1 className="gps-heading text-3xl md:text-5xl">{user?.name?.toUpperCase()}</h1>
            <div className="text-mute text-xs gps-mono mt-2 tracking-[0.2em]">
              {user?.gender} · {user?.country || "GLOBAL"}{user?.gym ? ` · ${user.gym}` : ""}
            </div>
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <button onClick={() => setEditOpen(true)} className="gps-btn ghost" data-testid="edit-profile-open">
            <UserCog size={14} /> EDIT PROFILE
          </button>
          <button onClick={() => setShowInput((s) => !s)} className="gps-btn ghost" data-testid="toggle-input">
            <Activity size={14} /> {showInput ? "VIEW RESULTS" : "ENTER SCORES"}
          </button>
          {gps && (
            <button onClick={openCurrentCard} className="gps-btn" data-testid="open-player-card">
              <IdCard size={14} /> GPS CARD
            </button>
          )}
        </div>
      </div>

      <EditProfileDialog open={editOpen} onClose={() => setEditOpen(false)} onSaved={load} />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mt-10">
        <GlassPanel className="p-8 lg:col-span-1" scanline data-testid="gps-overall-card">
          <div className="flex items-center gap-1.5">
            <div className="gps-label">GPS OVERALL</div>
            <InfoHint label="About GPS Overall">
              <span className="text-white">GPS = General Physicality Score.</span> A single 0–100 number
              summarising your physical capability across all 5 pillars. Calculated as the average
              of your strength, speed, explosive, stamina and control sub-scores from your most
              recent test log.
            </InfoHint>
          </div>
          <div className="text-white mt-4 flex items-center gap-3 flex-wrap">
            <ScouterNumber value={gps?.gps ?? 0} decimals={1} className="text-7xl" />
            {profile?.history?.[0] && (
              profile.history[0].verified ? (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-cyan-400/60 bg-cyan-400/10 gps-mono text-[10px] tracking-[0.3em] text-cyan-300"
                  data-testid="gps-overall-verified"
                  style={{ boxShadow: "0 0 12px rgba(0,240,255,0.25)" }}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-cyan-300" style={{ boxShadow: "0 0 6px #00f0ff" }} />
                  VERIFIED
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-white/20 gps-mono text-[10px] tracking-[0.3em] text-mute"
                  data-testid="gps-overall-unverified"
                >
                  UNVERIFIED
                </span>
              )
            )}
          </div>
          <div className="text-mute text-xs gps-mono mt-2">/ 100</div>
          <div className="hud-divider my-6" />
          <div className="flex items-center gap-1.5">
            <div className="gps-label">ARCHETYPE</div>
            <InfoHint label="About Archetypes">
              One of 32 physical archetypes, assigned from the shape of your pillar profile. Your
              top 2 pillars determine the archetype family (e.g. Strength + Explosive → Bull).
              Visit the Archetypes page for the full list and trait descriptions.
            </InfoHint>
          </div>
          <div className="mt-2">
            <span className="gps-mono text-white tracking-[0.3em] text-sm">{gps?.archetype?.code || "?????"}</span>
            <div className="gps-heading text-3xl mt-1 text-white">{(gps?.archetype?.name || "—").toUpperCase()}</div>
            <div className="text-mute text-xs">{gps?.archetype?.tag || "Awaiting test"}</div>
          </div>
          <div className="hud-divider my-6" />
          <div className="flex items-center gap-1.5">
            <div className="gps-label">PERCENTILE</div>
            <InfoHint label="About Percentile">
              Where you rank among all GPS athletes globally. A percentile of 80% means your GPS
              is higher than 80% of all logged athletes on the platform.
            </InfoHint>
          </div>
          <div className="text-white mt-2 text-3xl">
            <ScouterNumber value={profile?.percentile ?? 0} decimals={1} suffix="%" />
          </div>
        </GlassPanel>

        <GlassPanel className="p-6 lg:col-span-2" data-testid="radar-card">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <div className="gps-label">PILLAR PROFILE</div>
              <InfoHint label="About Pillar Profile">
                Five-axis diagram showing your sub-scores across the 5 physicality pillars:
                Strength, Speed, Explosive, Stamina, Control. Each axis is scored 0–100 on the
                same Q/D/R curve as your GPS. The bigger and more balanced the shape, the more
                complete the athlete.
              </InfoHint>
            </div>
            <span className="gps-mono text-white text-[10px] tracking-[0.3em]">5-AXIS DIAGRAM</span>
          </div>
          <PillarRadar pillars={gps?.pillars || {}} height={360} />
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mt-4">
            {[
              { k: "strength", label: "STRENGTH" },
              { k: "speed", label: "SPEED" },
              { k: "explosive", label: "EXPLOSIVE" },
              { k: "stamina", label: "STAMINA" },
              { k: "control", label: "CONTROL" },
            ].map(({ k, label }) => (
              <div key={k} className="border border-white/15 rounded-lg p-3 text-center">
                <div className="gps-label text-[10px]">{label}</div>
                <div className="big-numeric text-white text-xl mt-1">
                  {gps?.pillars?.[k]?.toFixed(1) ?? "0.0"}
                </div>
              </div>
            ))}
          </div>
        </GlassPanel>
      </div>

      {/* Input form */}
      {showInput && (
        <GlassPanel className="mt-6 p-8" data-testid="score-input-card">
          <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
            <div>
              <div className="gps-label">CALIBRATION INPUT</div>
              <h2 className="gps-heading text-xl text-white">ENTER TEST RESULTS</h2>
            </div>
            <span className="gps-tag">FILL ANY · BOUNDED Q→R · BELOW Q SCORES 0</span>
          </div>

          <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {/* Body metrics — mandatory. Every score record captures these. */}
            <div className="md:col-span-3 lg:col-span-5 grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 rounded-md border border-cyan-500/30 bg-cyan-500/[0.03]">
              <div>
                <label className="gps-label flex items-center justify-between">
                  <span>HEIGHT <span className="text-white normal-case">(cm)</span></span>
                  <span className="text-red-400 text-[10px]">REQUIRED</span>
                </label>
                <input
                  type="number"
                  step="any"
                  min="80"
                  max="250"
                  required
                  className="gps-input"
                  placeholder="e.g. 178"
                  value={heightCm}
                  onChange={(e) => setHeightCm(e.target.value)}
                  data-testid="score-height-cm"
                />
                <div className="text-[10px] gps-mono text-mute mt-1 tracking-[0.15em]">
                  BETWEEN 80 – 250 CM
                </div>
              </div>
              <div>
                <label className="gps-label flex items-center justify-between">
                  <span>WEIGHT <span className="text-white normal-case">(kg)</span></span>
                  <span className="text-red-400 text-[10px]">REQUIRED</span>
                </label>
                <input
                  type="number"
                  step="any"
                  min="15"
                  max="250"
                  required
                  className="gps-input"
                  placeholder="e.g. 74"
                  value={weightKg}
                  onChange={(e) => setWeightKg(e.target.value)}
                  data-testid="score-weight-kg"
                />
                <div className="text-[10px] gps-mono text-mute mt-1 tracking-[0.15em]">
                  BETWEEN 15 – 250 KG
                </div>
              </div>
            </div>
            {testsMeta.map((t) => {
              const [q, d, r] = gender === "F" ? t.qdr_F : t.qdr_M;
              return (
                <div key={t.key}>
                  <label className="gps-label">
                    {t.name.toUpperCase()} <span className="text-white normal-case">({t.unit})</span>
                  </label>
                  <input
                    type="number"
                    step="any"
                    className="gps-input"
                    placeholder=""
                    value={values[t.key] ?? ""}
                    onChange={(e) => set(t.key, e.target.value)}
                    data-testid={`score-${t.key}`}
                  />
                  <div className="text-[10px] gps-mono text-mute mt-1 tracking-[0.15em]">
                    Q {q} · D {d} · R {r}
                  </div>
                </div>
              );
            })}
            <div className="md:col-span-3 lg:col-span-5 flex justify-end pt-2">
              <button type="submit" disabled={busy} className="gps-btn" data-testid="submit-scores">
                {busy ? "COMPUTING..." : "TEST & COMPUTE"}
              </button>
            </div>
          </form>
        </GlassPanel>
      )}

      {/* History — horizontal table with per-log GPS Card */}
      <GlassPanel className="mt-6 p-6" data-testid="history-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <div className="gps-label">TEST HISTORY</div>
            <InfoHint label="About Test History">
              Every test session you've logged, newest first. Each row shows the resulting GPS,
              archetype, the raw values for all 10 tests, and a VERIFICATION status. Only verified
              logs can generate a downloadable GPS Card.
            </InfoHint>
          </div>
          <span className="gps-tag">{profile?.history?.length || 0} TOTAL</span>
        </div>
        {profile?.history?.length > 0 ? (
          <div className="space-y-3">
            {profile.history.map((h) => (
              <div
                key={h.id}
                className="border border-white/12 rounded-md p-4"
                data-testid={`history-row-${h.id}`}
              >
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="big-numeric text-white text-2xl">{h.result?.gps?.toFixed(1)}</span>
                    <span className="gps-mono text-xs text-white">{h.result?.archetype?.code}</span>
                    <span className="text-mute text-xs">{h.result?.archetype?.name}</span>
                    {h.verified ? (
                      <span className="gps-tag" style={{ borderColor: "rgba(0,240,255,0.6)", color: "#00f0ff" }} data-testid={`history-verified-${h.id}`}>
                        <ShieldCheck size={11} className="inline -mt-0.5 mr-1" />
                        VERIFIED
                      </span>
                    ) : (
                      <span className="gps-tag" data-testid={`history-pending-${h.id}`}>
                        PENDING REVIEW
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="gps-mono text-[10px] text-mute tracking-[0.2em]">
                      {new Date(h.created_at).toLocaleString()}
                    </span>
                    {h.verified && (
                      <button
                        onClick={() => openLogCard(h)}
                        className="gps-btn ghost"
                        data-testid={`history-card-${h.id}`}
                        aria-label="Download GPS Card for this log"
                        title="Download GPS Card for this verified log"
                      >
                        <IdCard size={12} /> GPS CARD
                      </button>
                    )}
                    <button
                      onClick={() => deleteLog(h.id)}
                      className="gps-btn ghost"
                      data-testid={`delete-log-${h.id}`}
                      aria-label="Delete test log"
                    >
                      <Trash2 size={12} /> DELETE
                    </button>
                  </div>
                </div>
                {/* Body metrics — captured at submission time (mandatory) */}
                {(h.height_cm != null || h.weight_kg != null || h.snapshot?.height_cm != null) && (
                  <div className="flex flex-wrap gap-4 mt-3 text-[10px] gps-mono text-mute tracking-[0.15em]" data-testid={`history-body-${h.id}`}>
                    <span>HT · <span className="text-white">{(h.height_cm ?? h.snapshot?.height_cm ?? "—")} cm</span></span>
                    <span>WT · <span className="text-white">{(h.weight_kg ?? h.snapshot?.weight_kg ?? "—")} kg</span></span>
                  </div>
                )}
                {/* Raw test breakdown */}
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-5 gap-2 mt-4">
                  {testsMeta.map((t) => {
                    const v = h.raw?.[t.key];
                    return (
                      <div key={t.key} className="border border-white/8 rounded p-2">
                        <div className="gps-label text-[9px] truncate">{t.name.toUpperCase()}</div>
                        <div className="big-numeric text-white text-base mt-1">
                          {v != null ? `${v}${t.unit}` : "—"}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-10" data-testid="history-empty">
            <div className="gps-mono text-mute text-xs tracking-[0.3em]">NO TESTS RECORDED</div>
            <div className="text-mute text-xs mt-2">
              Submit your first test result above to start your test history.
            </div>
          </div>
        )}
      </GlassPanel>

      {/* Progress Chart — placed below history per athlete-terminal flow */}
      <GlassPanel className="mt-6 p-6" data-testid="progress-chart-card">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <div className="flex items-center gap-1.5">
            <div>
              <div className="gps-label">PROGRESS LOG</div>
              <h2 className="gps-heading text-xl text-white">SCORE EVOLUTION</h2>
            </div>
            <InfoHint label="About Progress Log">
              Tracks how your GPS and each of the 5 pillar sub-scores have evolved across every
              test you've logged. Unlocks once you have at least 2 tests on record. Use it to
              spot training trends, plateaus, and breakouts over time.
            </InfoHint>
          </div>
          <span className="gps-tag">{chartData.length} {chartData.length === 1 ? "TEST" : "TESTS"} · CHRONOLOGICAL</span>
        </div>
        {chartData.length >= 2 && (
          <div className="flex flex-wrap gap-2 mb-4" data-testid="progress-series-filter">
            {PROGRESS_SERIES.map((s) => {
              const on = visibleSeries[s.k];
              return (
                <button
                  key={s.k}
                  type="button"
                  onClick={() => toggleSeries(s.k)}
                  data-testid={`progress-toggle-${s.k}`}
                  aria-pressed={on}
                  className="gps-mono text-[10px] tracking-[0.25em] px-3 py-1.5 rounded-full border transition-colors flex items-center gap-2"
                  style={{
                    borderColor: on ? PILLAR_COLORS[s.k] : "rgba(255,255,255,0.18)",
                    background: on ? `${PILLAR_COLORS[s.k]}1F` : "transparent",
                    color: on ? PILLAR_COLORS[s.k] : "rgba(255,255,255,0.5)",
                  }}
                >
                  <span
                    aria-hidden
                    className="inline-block w-2 h-2 rounded-full"
                    style={{
                      background: on ? PILLAR_COLORS[s.k] : "transparent",
                      border: `1px solid ${PILLAR_COLORS[s.k]}`,
                    }}
                  />
                  {s.label}
                </button>
              );
            })}
          </div>
        )}
        {chartData.length >= 2 ? (
          <div style={{ width: "100%", height: 320, minWidth: 0 }}>
            <ResponsiveContainer width="100%" height="100%" minHeight={300}>
              <LineChart data={chartData} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" strokeDasharray="3 3" />
                <XAxis
                  dataKey="date"
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fill: "rgba(255,255,255,0.55)", fontFamily: "JetBrains Mono, monospace", fontSize: 10 }}
                />
                <YAxis
                  domain={[0, 100]}
                  stroke="rgba(255,255,255,0.4)"
                  tick={{ fill: "rgba(255,255,255,0.55)", fontFamily: "JetBrains Mono, monospace", fontSize: 10 }}
                />
                <Tooltip
                  contentStyle={{
                    background: "rgba(10,14,22,0.95)",
                    border: "1px solid rgba(0,240,255,0.35)",
                    fontFamily: "JetBrains Mono, monospace",
                    fontSize: 11,
                  }}
                  labelStyle={{ color: "#fff" }}
                />
                <Legend wrapperStyle={{ fontFamily: "JetBrains Mono, monospace", fontSize: 10, letterSpacing: "0.15em" }} />
                {PROGRESS_SERIES.filter((s) => visibleSeries[s.k]).map((s) => (
                  <Line
                    key={s.k}
                    type="monotone"
                    dataKey={s.k}
                    stroke={PILLAR_COLORS[s.k]}
                    strokeWidth={s.w}
                    dot={false}
                    name={s.label}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        ) : (
          <div className="text-center py-10" data-testid="progress-chart-empty">
            <div className="gps-mono text-mute text-xs tracking-[0.3em]">
              {chartData.length === 0 ? "NO TESTS YET" : "LOG ONE MORE TEST TO PLOT EVOLUTION"}
            </div>
            <div className="text-mute text-xs mt-2">
              Your progress chart unlocks at 2+ tests across GPS and the 5 pillars.
            </div>
          </div>
        )}
      </GlassPanel>

      {/* GPS Card Modal — responsive scaling so it always fits the viewport */}
      {cardPayload && (
        <div
          className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm overflow-y-auto overscroll-contain"
          onClick={() => setCardPayload(null)}
          data-testid="player-card-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="min-h-full flex flex-col items-center justify-start pt-20 sm:pt-24 pb-8 px-4 gap-5"
          >
            <div className="w-full max-w-md flex items-center justify-between">
              <div className="gps-tag">{cardPayload.source === "log" ? "GPS CARD · LOG" : "GPS CARD"}</div>
              <button
                onClick={() => setCardPayload(null)}
                className="grid place-items-center w-9 h-9 rounded-full border border-white/15 bg-black/40 text-mute hover:text-white hover:border-white/40 transition-colors"
                data-testid="player-card-close"
                aria-label="Close"
              >
                <X size={18} />
              </button>
            </div>

            {/* Scaled wrapper — reserves space equal to scaled size so layout doesn't break */}
            <div
              style={{
                width: CARD_W * cardScale,
                height: CARD_H * cardScale,
              }}
            >
              <div
                style={{
                  transform: `scale(${cardScale})`,
                  transformOrigin: "top left",
                  width: CARD_W,
                  height: CARD_H,
                }}
              >
                <PlayerCard
                  name={cardPayload.name}
                  avatarUrl={cardPayload.avatarUrl}
                  date={cardPayload.date}
                  gps={cardPayload.gps}
                  archetype={cardPayload.archetype}
                  pillars={cardPayload.pillars}
                  country={cardPayload.country}
                  gym={cardPayload.gym}
                  verified={cardPayload.verified}
                  publicId={cardPayload.publicId}
                />
              </div>
            </div>

            {/* Hidden full-resolution copy used purely for PNG export */}
            <div
              aria-hidden
              style={{
                position: "fixed",
                left: -99999,
                top: 0,
                pointerEvents: "none",
                opacity: 0,
              }}
            >
              <div ref={cardRef}>
                <PlayerCard
                  name={cardPayload.name}
                  avatarUrl={cardPayload.avatarUrl}
                  date={cardPayload.date}
                  gps={cardPayload.gps}
                  archetype={cardPayload.archetype}
                  pillars={cardPayload.pillars}
                  country={cardPayload.country}
                  gym={cardPayload.gym}
                  verified={cardPayload.verified}
                  publicId={cardPayload.publicId}
                />
              </div>
            </div>

            <button
              onClick={downloadCard}
              disabled={cardBusy}
              className="gps-btn"
              data-testid="player-card-download"
            >
              <Download size={14} /> {cardBusy ? "EXPORTING..." : "DOWNLOAD PNG"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
